import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import Users from '../../Constants/Endpoints/Users';
import GetUserImpersonatablesUsingEmailRequest from '../../Interfaces/Requests/Users/GetUserImpersonatablesUsingEmail';
import GetUserImpersonatablesUsingEmailResponse from '../../Interfaces/Responses/Users/GetUserImpersonatablesUsingEmail';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import {
    Alert as ServaAlert,
    Button,
    Col,
    Divider,
    Input,
    Option,
    Row,
    Select,
    Skeleton,
    Text,
} from '@servahealth/serva-ui-kit';
import { RollbackOutlined } from '@ant-design/icons';
import { UserSwitchOutlined } from '@ant-design/icons';
import '../../Css/Loading.css';

interface AccountImpersonateUserProps {
    selectedImpersonatable: string;
    onSelect: (val: string) => void;
    onApply: () => void;
    onRevert: () => void;
    onCancel: () => void;
}

const AccountImpersonateUser = ({ selectedImpersonatable, onSelect, onApply, onRevert, onCancel }: AccountImpersonateUserProps) => {
    const { user } = useAuth();

    const [userImpersonatables, setUserImpersonatables] = useState<GetUserImpersonatablesUsingEmailResponse[]>([]);

    const { mutate: getUserImpersonatables, isLoading: isGetUserImpersonatablesLoading, isError: isGetUserImpersonatablesError, error: getUserImpersonatablesError, } = useMutation(
        async () => {
            let responseData: Promise<GetUserImpersonatablesUsingEmailResponse[]>;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Users.GET_USER_IMPERSONATABLES_USING_EMAIL}`;
                const requestData: GetUserImpersonatablesUsingEmailRequest = { gui_userid: user?.email.toString() ?? '', };
                responseData = AxiosUtil.get<GetUserImpersonatablesUsingEmailResponse[], GetUserImpersonatablesUsingEmailRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_USER_IMPERSONATABLES_USING_EMAIL: [Get User Impersonatables] Internal Error: ' + err);
                console.error('GET_USER_IMPERSONATABLES_USING_EMAIL [Get User Impersonatables] Internal Error: ', err);
                responseData = Promise.reject();
            }
            return responseData;
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_USER_IMPERSONATABLES_USING_EMAIL: [Get User Impersonatables] Internal Error: ' + err);
                console.error('GET_USER_IMPERSONATABLES_USING_EMAIL [Get User Impersonatables] Internal Error: ', err);
            },
            onSuccess: (response: GetUserImpersonatablesUsingEmailResponse[]) => {
                setUserImpersonatables(response);
            },
        }
    );

    const [currentError, setCurrentError] = useState('');

    useEffect(() => {
        if (user?.currentToken) {
            getUserImpersonatables();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSearch = (value: string) => {
        // console.log('search:', value);
    };

    const determineWhichError = () => {
        if ( isGetUserImpersonatablesError ) {
            return currentError + ' ' + (getUserImpersonatablesError as any).response?.data.message;
        }
        else {
            return '';
        }
    } 

    return (
        <>
            {
                isGetUserImpersonatablesError
                &&
                <ServaAlert
                    className={'mb-2'}
                    message={determineWhichError()}
                    showIcon={true}
                    type="error"
                ></ServaAlert>
            }
            {
                isGetUserImpersonatablesLoading
                ?
                <Skeleton paragraph={{ rows: 6 }} active />
                :
                <>
                    <Divider style={{ borderTop: '1px solid black', margin: '8px 0' }}></Divider>
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={4} style={{display: 'flex', justifyContent: 'right'}}>
                            <Text italic style={{paddingTop: '8px'}}>Current User:</Text>
                        </Col>
                        <Col span={8}>
                            <Input
                                readOnly
                                value={user?.email}
                                style={{paddingTop: '4px', width: '100%'}}
                            />
                        </Col>
                        <Col span={4} style={{display: 'flex', justifyContent: 'right'}}>
                            <Text italic style={{paddingTop: '8px'}}>Running User:</Text>
                        </Col>
                        <Col span={8}>
                            <Input
                                readOnly
                                value={user?.runningUser?.email}
                                style={{paddingTop: '4px', width: '100%'}}
                            />
                        </Col>
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={4} style={{display: 'flex', justifyContent: 'right'}}>
                            <Text style={{paddingTop: '8px'}}>Impersonate:</Text>
                        </Col>
                        <Col span={8}>
                            <Select
                                defaultValue={user?.runningUser?.id.toString()}
                                filterOption={true}
                                optionFilterProp='label'
                                showSearch
                                style={{paddingTop: '4px', width: '100%'}}
                                virtual={false}
                                onSearch={onSearch}
                                onSelect={onSelect}
                            >
                                {
                                    userImpersonatables.map
                                    (
                                        eachImpersonatable =>
                                            <Option
                                                label={eachImpersonatable.display}
                                                key={eachImpersonatable.value}
                                                value={eachImpersonatable.value}>{eachImpersonatable.display}
                                            </Option>
                                    )
                                }
                            </Select>
                        </Col>
                        <Col span={12}/>
                    </Row>
                    <Divider style={{ borderTop: '1px solid black', margin: '8px 0' }}></Divider>
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={8}>
                            <Button
                                block
                                type="default"
                                onClick={onCancel}
                            >
                                Close
                            </Button>
                        </Col>
                        <Col span={8}>
                            <Button
                                block
                                disabled={user?.id === user?.runningUser?.id}
                                icon={<RollbackOutlined />}
                                type="default"
                                onClick={onRevert}
                            >
                                Revert To Current
                            </Button>
                        </Col>
                        <Col span={8}>
                            <Button
                                block
                                disabled={(selectedImpersonatable || user?.runningUser?.id.toString() || '') === user?.runningUser?.id.toString()}
                                icon={<UserSwitchOutlined />}
                                type='primary'
                                onClick={onApply}
                            >
                                Impersonate User
                            </Button>
                        </Col>
                    </Row>
                </>
            }
        </>
    );
};

export default AccountImpersonateUser;
